import type { CoursePlan, PlanUnit, SessionCurriculumSlice } from '../types/coursePlan';
import type { CurriculumTarget, GuideGenerationMode } from '@interactive-room';
import {
  createEmptyPlanSession,
  createEmptyPlanUnit,
} from '../services/coursePlanStorage';

export interface AiLearningStop {
  title: string;
  focus: string;
  evidence: string;
}

export interface AiUnitPlanSession {
  title: string;
  notes?: string;
  /** Presente cuando la respuesta viene como Learning Path sin normalizar */
  focus?: string;
  evidence?: string;
  curriculumSlice?: SessionCurriculumSlice;
}

export interface AiUnitPlanResponse {
  title: string;
  objective: string;
  sessions: AiUnitPlanSession[];
  learningStops?: AiLearningStop[];
}

/** Formato notes de la línea de tiempo a partir de un hito de la ruta. */
export function formatLearningStopNotes(focus: string, evidence: string): string {
  const safeFocus = focus.trim();
  const safeEvidence = evidence.trim();
  if (safeFocus && safeEvidence) {
    return `Foco: ${safeFocus}\n\nEvidencia: ${safeEvidence}`;
  }
  return safeFocus || safeEvidence;
}

export function applyAiUnitToPlan(
  plan: CoursePlan,
  aiUnit: AiUnitPlanResponse,
  options?: {
    generationMode?: GuideGenerationMode;
    curriculumTarget?: CurriculumTarget;
  },
): PlanUnit {
  const unit = createEmptyPlanUnit(plan.units.length);
  unit.title = aiUnit.title.trim() || 'Ruta de aprendizaje';
  unit.objective = aiUnit.objective.trim();
  if (options?.generationMode) unit.generationMode = options.generationMode;
  if (options?.curriculumTarget) unit.curriculumTarget = options.curriculumTarget;
  unit.sessions = aiUnit.sessions.map((session, index) => {
    const focus = session.focus?.trim() ?? '';
    const evidence = session.evidence?.trim() ?? '';
    const notesFromStop =
      focus || evidence ? formatLearningStopNotes(focus, evidence) : session.notes?.trim() ?? '';

    return {
      ...createEmptyPlanSession(index),
      title: session.title.trim() || `Hito ${index + 1}`,
      notes: notesFromStop,
      ...(session.curriculumSlice ? { curriculumSlice: session.curriculumSlice } : {}),
    };
  });
  return unit;
}

export const UNIT_SESSION_COUNT_OPTIONS = [4, 5, 6, 7, 8] as const;
